import { useContext, useEffect, useState } from 'react';
import { useConfig } from '../Config/ConfigProvider';
import FallbackNextgenImage from './FallbackNextgenImage';
import { NextgenImageContext, NextgenImageProvider } from './NextgenImageProvider';
import { LoaderType, NextgenImageProps } from './types/propsTypes';

export const replaceCSToCDN = (url: string, useCDNForCS: boolean, csImageUrl: string, CDN_URL: string) =>
  useCDNForCS && url?.includes(csImageUrl) ? url.replace(csImageUrl, CDN_URL) : url;

const getContentStackURL = (
  src: string,
  useCDNForCS: boolean,
  CDN_URL: string,
  csImageUrl: string,
  width: number,
  format: string,
  quality: NextgenImageProps['quality'],
  fallbackQuality: number,
  isRelativeURL: boolean,
) => {
  const queryString = `quality=${quality || fallbackQuality}&width=${width}&format=${format}`;
  if (isRelativeURL) {
    return `${useCDNForCS ? CDN_URL : csImageUrl}${src}?${queryString}`;
  } else {
    const delimiter = src.includes('?') ? '&' : '?';
    return `${src}${delimiter}${queryString}`;
  }
};

const getQuality = (loaderType: NextgenImageProps['loaderType'], quality: NextgenImageProps['quality']) =>
  loaderType === LoaderType.CONTENTSTACK || !quality ? {} : { quality };

const getObserverActions = (
  imageElement: Element | null,
  restProps: Partial<NextgenImageProps>,
  hasBeenObserved: boolean,
) => !imageElement || restProps.priority || hasBeenObserved;

const NextgenImage = ({
  fill = true,
  quality = 100,
  // TODO: Make CONTENTSTACK value default after PODs make their changes
  loaderType,
  isRelativeURL = false,
  fallbackQuality = 100,
  format = 'avif',
  src,
  ...restProps
}: NextgenImageProps) => {
  const { element: NextJsImage, useCDNForCS } = useContext(NextgenImageContext);
  const configs = useConfig();
  const [isAboveFold, setIsAboveFold] = useState(false);
  const [hasBeenObserved, setHasBeenObserved] = useState(false); // Track if image has been observed

  const csImageUrl = configs.NEXT_PUBLIC_CONTENT_STACK_IMAGES_URL as string;
  const CDNPostFix = configs.NEXT_PUBLIC_CONTENT_STACK_CDN_POSTFIX as string;
  const CDN_URL = `${configs.DEFAULT_HOST_URL}${CDNPostFix}`;

  const updatedSRC = replaceCSToCDN(src as string, useCDNForCS, csImageUrl, CDN_URL);

  useEffect(() => {
    const imageElement = document.querySelector(`img[src="${updatedSRC}"]`);
    if (getObserverActions(imageElement, restProps, hasBeenObserved)) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsAboveFold(entry.isIntersecting);
        setHasBeenObserved(true);
      },
      {
        rootMargin: '0px',
        threshold: 0.2, // Trigger when 20% of the image is in view
      },
    );

    observer.observe(imageElement as Element);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [updatedSRC, restProps.priority, hasBeenObserved]);

  const customLoader = ({ src, width }: { src: string; width: number }) => {
    if (loaderType === LoaderType.CONTENTSTACK) {
      return getContentStackURL(
        src,
        useCDNForCS,
        CDN_URL,
        csImageUrl,
        width,
        format,
        quality,
        fallbackQuality,
        isRelativeURL,
      );
    } else if (loaderType === LoaderType.WEBFONTS && isRelativeURL) {
      return `${configs.NEXT_ASSETS_BASE_PATH}${src}`;
    }

    return updatedSRC;
  };

  const handleLazyLoading = () => {
    if (isAboveFold || restProps.priority) {
      return undefined;
    }

    return restProps.loading ?? 'lazy';
  };

  const renderNextImage = NextJsImage && !!updatedSRC;
  const noFilledImage = restProps.height && restProps.width;

  return renderNextImage ? (
    <NextJsImage
      src={updatedSRC}
      loader={customLoader}
      {...getQuality(loaderType, quality)}
      fill={noFilledImage ? false : fill}
      priority={isAboveFold}
      {...restProps}
      loading={handleLazyLoading()}
    />
  ) : (
    <FallbackNextgenImage src={updatedSRC} {...restProps} />
  );
};

export { NextgenImage, NextgenImageProvider, LoaderType };
export type { NextgenImageProps };
